/**
 * Card Component - Reusable card container
 *
 * Variants:
 * - default: Standard card with shadow
 * - outline: Outlined card
 * - elevated: Elevated card with larger shadow
 * - flat: Flat card without shadow
 *
 * Sizes:
 * - sm: Small padding
 * - md: Medium padding (default)
 * - lg: Large padding
 * - xl: Extra large padding
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const cardVariants = cva(
  'rounded-lg bg-white',
  {
    variants: {
      variant: {
        default: 'border border-neutral-200 shadow-sm',
        outline: 'border-2 border-neutral-200',
        elevated: 'border border-neutral-200 shadow-md',
        flat: 'border-0 shadow-none',
      },
      size: {
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
        xl: 'p-10',
      },
      hover: {
        true: 'transition-all duration-200 hover:shadow-md hover:border-primary-300 cursor-pointer',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hover: false,
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  noPadding?: boolean;
}

/**
 * Card Component
 *
 * @example
 * ```tsx
 * <Card>Card content</Card>
 * <Card variant="elevated" size="lg">Elevated card</Card>
 * <Card header={<h2>Title</h2>} footer={<Button>Action</Button>}>
 *   Content
 * </Card>
 * <Card hover clickable>Clickable card</Card>
 * ```
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, hover, header, footer, children, noPadding, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({ variant, hover }),
          !noPadding && cardVariants({ size }),
          className
        )}
        {...props}
      >
        {header && <div className="mb-4">{header}</div>}
        {children}
        {footer && <div className="mt-4 pt-4 border-t border-neutral-200">{footer}</div>}
      </div>
    );
  }
);

Card.displayName = 'Card';

export { Card, cardVariants };
export default Card;
